import type { CosignatureQueueItem, UUID } from "@/core/types";
import dayjs from "@/lib/helpers/dayjs";
import { isCosignatureStatus } from "@/core/helpers/(predicates)/cosignature";

import { DocumentTypeMap } from "../constants";

export function adaptQueueData(items: CosignatureQueueItem[]) {
  // dev-era artifacts (e.g. "published") can leak into responses — drop rows
  // with a status outside the known enum rather than crash on an unmapped badge/action
  return items
    .filter((item) => isCosignatureStatus(item.cosignature_status))
    .map((item) => {
      const base = {
        dateSigned: item.signature_date ? dayjs(item.signature_date).format("MM/DD/YYYY") : null,
        daysPending: item.signature_date ? dayjs().diff(dayjs(item.signature_date), "day") : null,
        id: item.document_id,
        patientId: item.patient_id,
        patientName: item.patient_name,
        // raw ISO string used for correct date sort (dateSigned is MM/DD/YYYY, not lexicographically sortable)
        signatureDate: item.signature_date ?? null,
        status: item.cosignature_status,
        submittedBy: item.provider_name,
        type: DocumentTypeMap[item.document_type],
      };

      // backend guarantees related_note_id whenever document_type is note_addendum —
      // items missing it are dropped server-side before reaching this response, never
      // emitted with a null value (see get_cosignature_queue.py:_resolve_document)
      return item.document_type === "note_addendum"
        ? {
            ...base,
            documentType: "note_addendum" as const,
            relatedNoteId: item.related_note_id as UUID,
          }
        : // `as null` prevents widening to `any` (strict/strictNullChecks is off in this repo)
          { ...base, documentType: "note" as const, relatedNoteId: null as null };
    });
}

export type AdaptedQueueRow = ReturnType<typeof adaptQueueData>[number];

export function getNoteId(row: AdaptedQueueRow): UUID {
  return row.documentType === "note_addendum" ? row.relatedNoteId : row.id;
}
